import { useState, useCallback } from 'react';
import { trialAPI } from '../services/api';

/**
 * Hook to manage trial-protected actions
 * Checks trial status before allowing actions and handles 403 responses
 */
const useTrialGuard = () => {
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState(null);
  const [paywallAction, setPaywallAction] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);

  // Check if an action is allowed before executing
  const checkAction = useCallback(async (actionType) => {
    try {
      const response = await trialAPI.checkAction(actionType);
      return { allowed: true, data: response.data };
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail || {};
        setPaywallReason(detail.reason || 'actions');
        setPaywallAction(actionType);
        setShowPaywall(true);
        return { allowed: false, error: detail };
      }
      throw error;
    }
  }, []);

  // Load current trial status
  const loadTrialStatus = useCallback(async () => {
    try {
      const response = await trialAPI.getStatus();
      setTrialStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load trial status:', error);
      return null;
    }
  }, []);

  // Handle API error - check if it's a trial limit error
  const handleTrialError = useCallback((error, actionType = null) => {
    if (error.response?.status === 403) {
      const detail = error.response.data?.detail || {};
      if (detail.code === 'TRIAL_LIMIT_REACHED') {
        setPaywallReason(detail.reason || 'actions');
        setPaywallAction(actionType);
        setShowPaywall(true);
        return true;
      }
    }
    return false;
  }, []);

  // Close paywall modal
  const closePaywall = useCallback(() => {
    setShowPaywall(false);
    setPaywallReason(null);
    setPaywallAction(null);
  }, []);

  // Execute a protected action
  const executeProtected = useCallback(async (actionType, asyncFn) => {
    try {
      return await asyncFn();
    } catch (error) {
      if (handleTrialError(error, actionType)) {
        return { blocked: true, error };
      }
      throw error;
    }
  }, [handleTrialError]);

  return {
    // State
    showPaywall,
    paywallReason,
    paywallAction,
    trialStatus,
    
    // Actions
    checkAction,
    loadTrialStatus,
    handleTrialError,
    closePaywall,
    executeProtected
  };
};

export default useTrialGuard;
